import { P, match } from '@frontend/duck-tape';
import { CreateDraftTaskProvider, useDraftCreationFlow } from '@frontend/react-domain';
import { LoadingSpinnerPanel, YStack } from '@frontend/web-react';
import { Checkout } from '@frontend/web/components/DomainSpecific/DraftTasks/Checkout/Checkout';
import { Description } from '@frontend/web/components/DomainSpecific/DraftTasks/Description';
import { DraftCreationHeader } from '@frontend/web/components/DomainSpecific/DraftTasks/DraftCreationHeader';
import { InformationGathering } from '@frontend/web/components/DomainSpecific/DraftTasks/InformationGathering/InformationGathering';
import { useAccessControl, useAnalytics, useInvalidateTaskCaches, useLazyRetrieveDraftTaskQuery } from '@frontend/web/hooks';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

const DraftTaskContent = () => {
  const { draftTaskId } = Route.useParams();
  const search = useSearch({ strict: false }) as { source?: string };
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const invalidateTaskCaches = useInvalidateTaskCaches();
  const entryPoint = match(search.source)
    .with('home', () => 'home' as const)
    .with('new_task_page', () => 'new_task_page' as const)
    .with('im_feeling_ducky', () => 'im_feeling_ducky' as const)
    .otherwise(() => 'unknown' as const);

  const { currentParentStep, decrementParentStep, incrementParentStep, setupSocket, startOver } = useDraftCreationFlow({
    initialDraftTaskId: draftTaskId,
    onDraftAutoTransitioned: (taskId) => {
      invalidateTaskCaches(taskId);
      if (taskId) {
        track({ data: { entryPoint }, event: 'draftTaskCheckoutCompleted', topic: 'draftTasks' });
        navigate({ params: { taskId }, replace: true, to: '/app/task/$taskId' });
        return;
      }
      navigate({ to: '/app/home' });
    },
    socketClient: {
      disconnectSocket,
      getSocket,
      initializeSocket,
    },
  });

  return (
    <YStack bg="trueWhite" pb="xxxl" pt="md">
      <DraftCreationHeader currentStep={currentParentStep} />
      {match(currentParentStep)
        .with('description', () => <Description incrementParentStep={incrementParentStep} setupSocket={setupSocket} />)
        .with('information_gathering', () => (
          <InformationGathering decrementParentStep={decrementParentStep} incrementParentStep={incrementParentStep} />
        ))
        .with('checkout', () => (
          <Checkout
            decrementParentStep={decrementParentStep}
            entryPoint={entryPoint}
            incrementParentStep={incrementParentStep}
            startOver={startOver}
          />
        ))
        .with(P.nullish, () => <LoadingSpinnerPanel />)
        .exhaustive()}
    </YStack>
  );
};

const DraftTask = () => {
  const { checkAccessAndRedirect, isClientSubscribed } = useAccessControl();
  const navigate = useNavigate();
  const invalidateTaskCaches = useInvalidateTaskCaches();

  useEffect(() => {
    checkAccessAndRedirect();
  }, [isClientSubscribed]);

  if (!isClientSubscribed) {
    return null;
  }

  return (
    <CreateDraftTaskProvider
      onNavigateToTask={(taskId: string) => {
        invalidateTaskCaches(taskId);
        navigate({ params: { taskId }, replace: true, to: '/app/task/$taskId' });
      }}
      useLazyRetrieveDraftTaskQuery={useLazyRetrieveDraftTaskQuery}
    >
      <DraftTaskContent />
    </CreateDraftTaskProvider>
  );
};

export const Route = createFileRoute('/app/draft-task/$draftTaskId')({
  component: DraftTask,
});
